import is from '@sindresorhus/is';
import { z } from 'zod';

const commentAuthorSchema = z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
});

export type CommentAuthor = z.infer<typeof commentAuthorSchema>;

export const commentTargetDescriptionSectionSchema = z.object({
    kind: z.literal('description-section'),
    sectionId: z.string(),
});

export const commentTargetTitleSchema = z.object({
    kind: z.literal('title'),
});

export const commentTargetSkillSchema = z.object({
    kind: z.literal('skill'),
    skillId: z.string(),
});

export const commentTargetSchema = z.discriminatedUnion('kind', [
    commentTargetDescriptionSectionSchema,
    commentTargetTitleSchema,
    commentTargetSkillSchema,
]);

export type CommentTarget = z.infer<typeof commentTargetSchema>;

export const commentAnchorSchema = z.object({
    quoteSelector: z.object({
        exact: z.string().refine((value) => value.trim().length > 0, 'quoteSelector.exact must not be blank'),
        prefix: z.string().nullish(),
        suffix: z.string().nullish(),
    }),
    positionSelector: z
        .object({
            start: z.number().int().nonnegative(),
            end: z.number().int().nonnegative(),
        })
        .refine(({ start, end }) => end > start, {
            message: 'positionSelector.end must be greater than positionSelector.start',
            path: ['end'],
        }),
    status: z.enum(['anchored', 'unresolved', 'ambiguous']),
});

export type CommentAnchor = z.infer<typeof commentAnchorSchema>;

export const createCommentAnchorSchema = commentAnchorSchema.omit({ status: true });

export type CreateCommentAnchor = z.infer<typeof createCommentAnchorSchema>;

export const commentResolutionSchema = z.object({
    version: z.number().int().nonnegative(),
    resolvedAt: z.iso.datetime(),
    resolvedBy: commentAuthorSchema.nullish(),
});

export type CommentResolution = z.infer<typeof commentResolutionSchema>;

export const commentSchema = z.object({
    id: z.uuid(),
    parentId: z.uuid().nullish(),
    version: z.number().int().nonnegative(),
    sourceLanguage: z.string(),
    target: commentTargetSchema,
    content: z.string(),
    author: commentAuthorSchema,
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime().nullish(),
    updatedBy: commentAuthorSchema.nullish(),
    anchor: commentAnchorSchema.nullish(),
    resolution: commentResolutionSchema.nullish(),
});

export type Comment = z.infer<typeof commentSchema>;

export const commentsSchema = commentSchema.array();

export type Comments = z.infer<typeof commentsSchema>;

export const createCommentRequestSchema = z
    .object({
        id: z.uuid(),
        version: z.number().int().nonnegative(),
        sourceLanguage: z.string(),
        target: commentTargetSchema,
        content: z.string(),
        anchor: createCommentAnchorSchema.nullish(),
    })
    .superRefine((value, ctx) => {
        if (value.target.kind === 'description-section') {
            if (is.nullOrUndefined(value.anchor)) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'anchor is required for description-section targets',
                    path: ['anchor'],
                });
            }
            return;
        }
        if (!is.nullOrUndefined(value.anchor)) {
            ctx.addIssue({
                code: 'custom',
                message: 'anchor must be absent for non-description targets',
                path: ['anchor'],
            });
        }
    });

export type CreateCommentRequest = z.infer<typeof createCommentRequestSchema>;

export const createReplyRequestSchema = z.object({
    id: z.uuid(),
    content: z.string().min(1),
});

export type CreateReplyRequest = z.infer<typeof createReplyRequestSchema>;

export const updateReplyRequestSchema = z.object({
    content: z.string().min(1),
});

export type UpdateReplyRequest = z.infer<typeof updateReplyRequestSchema>;

export const resolveCommentRequestSchema = z.object({
    version: z.number().int().nonnegative(),
});

export type ResolveCommentRequest = z.infer<typeof resolveCommentRequestSchema>;
